declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>
  }
}

interface Offer {
  id: string | number
  name: string
  nickname?: string
  stripe_price_id?: string
}

const SUPPORTED_UTM_KEYS = [
  "utm_medium",
  "utm_source",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "utm_term",
] as const

const PURCHASE_STORAGE_PREFIX = "ga4-purchase:"
const pushedEvents = new Set<string>()

function dataLayer(): Array<Record<string, unknown>> {
  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

function utmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search) // eslint-disable-line compat/compat

  return SUPPORTED_UTM_KEYS.reduce<Record<string, string>>((memo, key) => {
    const value = params.get(key)
    if (!value) return memo

    return {
      ...memo,
      [key]: value,
    }
  }, {})
}

export function currentUtmParams(): Record<string, string> {
  return utmParams()
}

function amountFromMinorUnits(amount: number): number {
  return amount / 100
}

function buildItems(offer: Offer, amountMinorUnits: number): Array<Record<string, unknown>> {
  return [
    {
      item_id: offer.stripe_price_id || offer.id,
      item_name: "Snow-Forecast Membership",
      item_category: "subscription",
      item_variant: offer.nickname || offer.name,
      price: amountFromMinorUnits(amountMinorUnits),
      quantity: 1,
    },
  ]
}

function pushEvent(event: string, params: Record<string, unknown> = {}): void {
  dataLayer().push({
    event,
    ...utmParams(),
    ...params,
  })
}

function pushEcommerceEvent(
  event: string,
  ecommerce: Record<string, unknown>,
  params: Record<string, unknown> = {},
): void {
  dataLayer().push({ ecommerce: null })
  pushEvent(event, {
    ...params,
    ecommerce,
  })
}

function pushOnce(key: string, callback: () => void): void {
  if (pushedEvents.has(key)) return

  pushedEvents.add(key)
  callback()
}

function trackedPurchaseKey(transactionId: string): string {
  return `${PURCHASE_STORAGE_PREFIX}${transactionId}`
}

function purchaseAlreadyTracked(transactionId: string): boolean {
  try {
    return window.localStorage.getItem(trackedPurchaseKey(transactionId)) === "1"
  } catch {
    return false
  }
}

function markPurchaseTracked(transactionId: string): void {
  try {
    window.localStorage.setItem(trackedPurchaseKey(transactionId), "1")
  } catch {
    // Ignore storage failures; the in-memory pushOnce guard still prevents duplicates in this page.
  }
}

export function trackBeginCheckout(
  offer: Offer,
  currencyCode: string,
  amountMinorUnits: number,
  coupon?: string | null,
): void {
  pushOnce(`begin_checkout:${offer.id}:${currencyCode}:${amountMinorUnits}:${coupon || ""}`, () => {
    pushEcommerceEvent("begin_checkout", {
      currency: currencyCode.toUpperCase(),
      value: amountFromMinorUnits(amountMinorUnits),
      coupon,
      items: buildItems(offer, amountMinorUnits),
    })
  })
}

export function trackAddPaymentInfo(
  offer: Offer,
  currencyCode: string,
  amountMinorUnits: number,
  paymentType: string,
  coupon?: string | null,
): void {
  pushEcommerceEvent("add_payment_info", {
    currency: currencyCode.toUpperCase(),
    value: amountFromMinorUnits(amountMinorUnits),
    coupon,
    payment_type: paymentType,
    items: buildItems(offer, amountMinorUnits),
  })
}

export function trackPromoCodeOpen(): void {
  pushEvent("promo_code_open")
}

export function trackPromoCodeApplied(params: Record<string, unknown> = {}): void {
  const key = `promo_code_applied:${params.promotion_code || params.promotion_code_id || ""}`
  pushOnce(key, () => {
    pushEvent("promo_code_applied", params)
  })
}

export async function trackCheckoutPurchase(
  sessionId: string | null,
  params: Record<string, unknown> = {},
): Promise<void> {
  if (!sessionId) return

  try {
    const response = await fetch(
      `/sign_up/stripe_payment/checkout_session_ecommerce?session_id=${encodeURIComponent(
        sessionId,
      )}`,
      {
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      },
    )
    if (!response.ok) return

    const ecommerce = (await response.json()) as Record<string, unknown>
    const transactionId = String(ecommerce.transaction_id || sessionId)
    if (purchaseAlreadyTracked(transactionId)) return

    pushOnce(`purchase:${transactionId}`, () => {
      pushEcommerceEvent("purchase", ecommerce, params)
      markPurchaseTracked(transactionId)
    })
  } catch {
    // Analytics must not interrupt the Checkout return flow.
  }
}

export {}
